import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Alert, App as AntdApp, Button, Checkbox, Dropdown, Input, Modal, Tooltip } from 'antd';
import {
	closestCenter,
	DndContext,
	pointerWithin,
	PointerSensor,
	useDraggable,
	useDroppable,
	useSensor,
	useSensors,
} from '@dnd-kit/core';
import { CSS } from '@dnd-kit/utilities';

import DataTypeBadge from '../common/DataTypeBadge';
import DialogLoadState from '../common/DialogLoadState';
import { Skeleton } from '../loading';
import RecordBadge from '../common/RecordBadge';
import ItemReadView from '../items/ItemReadView';
import FormLayoutPreview from './FormLayoutPreview';
import FormWidthControl from './FormWidthControl';
import { CHEVRON_DOWN, CHEVRON_UP, CLOSE, DRAG_HANDLE, KEBAB, PLUS } from '../icons';
import { deleteFormLayout, getFormLayout, putFormLayout } from '../../services/cardLayouts';
import { invalidateFormLayouts } from '../../hooks/useFormLayout';
import { useDirtyForm } from '../../hooks/useDirtyForm';
import { confirmDestructive } from '../common/confirmDestructive';
import { groupElementsIntoPanels } from '../../utils/elements';
import {
	addFormGroup,
	formLayoutFromPanels,
	formLayoutProblem,
	groupCollapsed,
	groupNames,
	leaveOutFormElement,
	leftOutList,
	MAX_GROUP_DESCRIPTION,
	MAX_GROUP_TITLE,
	moveFormElement,
	moveFormGroup,
	mustStayOnForm,
	normalizeFormLayoutBody,
	nudgeFormElement,
	OTHER_FIELDS_LABEL,
	placedFormNames,
	placementOf,
	placeRangeEnds,
	putBackFormElement,
	removeFormGroup,
	sameFormLayoutBody,
	setFormElementPlacement,
	setFormGroupCollapsed,
	updateFormGroup,
} from '../../utils/formLayout';
import { COLUMNS } from '../../utils/formGrid/limits';
import useUndoHistory from '../../hooks/useUndoHistory';
import useUndoShortcuts from '../../hooks/useUndoShortcuts';
import UndoRedoButtons from '../common/UndoRedoButtons';
import { dropAxis, dropSide, groupCells, limitSpan, planFormDrop, spanFromPointer, stepFormElement, stepSpan } from '../../utils/formGridEditor';
import { widthLimits, widthState, widthSummary } from '../../utils/formWidth';
import { normalizeId } from '../../utils/ids';
import { sampleElementValues } from '../../utils/displaySample';

/**
 * "How this card's form reads" — the editor for a card's FORM layout
 * (cardLayouts kind 'form'; docs/plans/form-layout.md, decision D8): the order
 * and groups of the item form and the item read view.
 *
 * A form layout lives at the CARD tier only and is inherited down the card
 * hierarchy, so the editor opens in one of three states and says which:
 *
 *   own        — this card's layout; Save updates it, "Reset" drops it (the
 *                card falls back to its parent's, or to automatic).
 *   inherited  — an ancestor's, shown read-only with "Customize for this
 *                card", which starts an override seeded from it.
 *   automatic  — nobody has arranged it; the editor starts from the
 *                automatic grouping written down, so arranging begins from
 *                what is on screen.
 *
 * Every element the card defines, inherits or attaches through a set is here
 * with where it came from ("from Person", "from Contact"). A group is a canvas
 * of twelve columns that draws its elements the way the form will: each cell
 * as wide as the form gives it, in flow order, a new row where one asks for it
 * (KPE-023, KPR-585). Moving is a drag of the handle (@dnd-kit's pointer
 * sensor, touch included) with a bar on the edge it will land on, AND the
 * keys on a focused handle — Left/Right and Up/Down move it earlier or later,
 * Shift with Left/Right makes it a column narrower or wider — AND a ⋯ menu per
 * row — Move up · Move down · Move to <group> · Leave unplaced — which is
 * also what the tests drive where a drag cannot be. Sizing is the right edge:
 * it snaps to whole columns, stops at the element's minimum or maximum and
 * says why. Every edit is a step in an undo history (Undo, Redo, Cmd/Ctrl+Z).
 * An element left unplaced is never lost: it lands in "Other fields" after
 * the groups, and so does anything a parent or a set adds later.
 *
 * Each placed element also carries its WIDTH (KPE-023, KPR-584): a chip that
 * says what it is drawn at and opens FormWidthControl under the group — Auto,
 * the quick picks, a stepper, "Start on a new row" — held to the element's type
 * limits (the vendored table), with the stored width outside them flagged.
 * A group's `elements` are ENTRIES (a name or `{ element, span?, newRow? }`):
 * every edit moves and writes the entry, never the bare name, so a width set
 * anywhere survives. The preview is FormLayoutPreview: the same read view at
 * desktop, tablet and phone widths.
 *
 * Saving is immediate and outside the card's one draft (the tile layout's
 * rule): the layout is its own record, validated against the SAVED card.
 * A save or a reset that lands CLOSES the editor — the operation is done,
 * and a Cancel after a Save read as "undo" (live pass, 2026-09-26). A
 * refusal keeps it open with the reason.
 *
 * LEFT OUT (KPR-1211). On a child card, an element it INHERITS (`fromCard`)
 * that the save can never demand (utils/formLayout mayLeaveOut) can be left off
 * this card's form: its ⋯ says **Leave out of this form** (shown disabled, with
 * the reason, for a required one), or it is dragged into the dashed **Left out
 * of this form** box after Other fields. Each row there has **Put back on the
 * form** and **Move to <group>**. The box is drawn only on a card that inherits
 * something. A stored name that is no longer eligible (made required, or
 * redeclared by the card) opens back in Other fields, and opening is not an
 * edit; the API drops it on the next save.
 *
 * "Start expanded" is two boxes per group — in the form, in the read view —
 * both ticked unless the stored body says otherwise, because a group read
 * rarely may still be one people fill in. Every group folds either way; this
 * is only where it begins. The boxes are the inverse of the stored
 * `collapsed*` flags (utils/formLayout groupCollapsed / setFormGroupCollapsed),
 * so nothing stored changes and a layout without the flags starts expanded.
 */

const OTHER = '__other__';
const LEFT = '__left_out__';
const itemId = name => `el:${name}`;
const nameOf = id => String(id).slice(3);
const containerId = groupId => `group:${groupId}`;
const groupIdOf = id => String(id).slice(6);

// A canvas narrower than this draws one column (the phone's rule): the handles
// are hidden there and the width controls and ⋯ do the job. The number is held
// to form-layout.css by a test.
export const CANVAS_COLUMNS_MIN_WIDTH = 520;

const ARROWS = { ArrowLeft: -1, ArrowUp: -1, ArrowRight: 1, ArrowDown: 1 };

const DND_ACCESSIBILITY = {
	screenReaderInstructions: {
		draggable: 'Drag to move this field. With the handle focused, the arrow keys move it earlier or later and Shift with Left or Right makes it narrower or wider.',
	},
};

function originOf(entry) {
	if (entry?.fromSet) return `from ${entry.fromSet?.name || 'an element set'}`;
	if (entry?.fromCard) return `from ${entry.fromCard?.name || 'a parent card'}`;
	return null;
}

function labelOf(element) {
	return element?.label?.singular || element?.name || '';
}

/** Why an inherited element cannot be left out, as the menu says it (KPR-1211). */
function stayReason(element) {
	const why = mustStayOnForm(element);
	if (why === 'required') return 'Required fields stay on the form.';
	if (why === 'requiredWhen') return 'Required when another field is filled, so it stays.';
	if (why === 'minEntries') return 'It needs entries, so it stays on the form.';
	return null;
}

/**
 * Where a drag is aimed. The pointer decides, not the geometry dnd-kit would
 * pick: the cell under it (a cell is never "closest" to a pointer that is on
 * another), else the nearest cell of the group it is in (a gap between cells,
 * the empty end of a row), else the group's own list (an empty group), else
 * nothing — a drop outside every group leaves the layout alone.
 */
export function collideWithPointer(args) {
	const within = pointerWithin(args);
	if (!within.length) return [];
	const self = within.find(hit => hit.id === args.active?.id);
	if (self) return [self];
	const cell = within.find(hit => String(hit.id).startsWith('el:'));
	if (cell) return [cell];
	const list = within.find(hit => String(hit.id).startsWith('group:'));
	if (!list) return [];
	const siblings = args.droppableContainers.filter(container => (
		String(container.id).startsWith('el:')
		&& container.id !== args.active?.id
		&& container.data?.current?.groupId === groupIdOf(list.id)
	));
	if (!siblings.length) return [list];
	const nearest = closestCenter({ ...args, droppableContainers: siblings });
	return nearest.length ? [nearest[0]] : [list];
}

/**
 * One element: a cell of the group's twelve-column canvas (`cell` says how many
 * columns it is drawn at), or a plain row of "Other fields" (`cell` null).
 *
 * The grip drags it and, focused, is the keyboard's handle on it (the arrow
 * keys move it, Shift with Left or Right resizes it). The right edge is the
 * resize handle. An `unknown` entry is a name the card's OWN stored layout
 * places that this browser's card cache does not have — it stays where it is,
 * marked, and is sent back as it was (see `seed`). The two halves of a date
 * range are two cells like any others: the form draws them as two fields
 * (KPR-626).
 */
function ElementCell({
	as: Tag = 'li', entry, groupId, index = 0, cell = null, limits = null, disabled, droppable = true,
	menuItems, onMenu, width = null, drop = null, live = null, active = false,
	onGripKeyDown, onGripBlur, onActivate, onResizeStart, onResizeLive, onResizeEnd,
}) {
	const name = entry.element.name;
	const { attributes, listeners, setActivatorNodeRef, setNodeRef: setDragRef, transform, isDragging } = useDraggable({ id: itemId(name), disabled });
	const { setNodeRef: setDropRef } = useDroppable({ id: itemId(name), disabled: disabled || !droppable, data: { groupId, name } });
	const node = useRef(null);
	const resize = useRef(null);
	const stopEscape = useRef(null);
	const setRefs = useCallback(target => {
		node.current = target;
		setDragRef(target);
		setDropRef(target);
	}, [setDragRef, setDropRef]);
	const label = labelOf(entry.element);
	const origin = originOf(entry);
	const resizable = Boolean(cell) && Boolean(limits) && !disabled && !entry.unknown;

	// Dragging the right edge. The edge follows the pointer in whole columns,
	// measured from where the cell began: the column pitch is read off the cell
	// itself (its width at its columns), so the list's padding and gap need not
	// be known, and a cell that wraps to another row while it grows does not
	// chase its own edge.
	const onResizeDown = event => {
		if (!resizable || (event.button !== undefined && event.button > 0)) return;
		event.preventDefault();
		event.stopPropagation();
		const rect = node.current.getBoundingClientRect();
		const gap = parseFloat(window.getComputedStyle(node.current.parentElement).columnGap) || 0;
		resize.current = { left: rect.left, width: rect.width, span: cell.span, start: cell.span, last: cell.span, gap, reason: null };
		// Capture keeps the edge following a pointer that strays off it; a
		// pointer the browser no longer tracks cannot be captured, and the
		// resize still works without it.
		try { event.currentTarget.setPointerCapture?.(event.pointerId); } catch (_) { /* not capturable */ }
		// Escape puts the width back, as it does a drag to reorder: the
		// listener is in the capture phase and stops the key, so it never
		// reaches the dialog's own Esc.
		const handle = event.currentTarget;
		const pointerId = event.pointerId;
		const onEscape = key => {
			if (key.key !== 'Escape') return;
			key.preventDefault();
			key.stopPropagation();
			try { handle.releasePointerCapture?.(pointerId); } catch (_) { /* never captured */ }
			finishResize(true);
		};
		document.addEventListener('keydown', onEscape, true);
		stopEscape.current = () => document.removeEventListener('keydown', onEscape, true);
		onResizeStart?.(name);
	};
	const onResizeMove = event => {
		const state = resize.current;
		if (!state) return;
		const raw = spanFromPointer({ x: event.clientX, left: state.left, width: state.width, span: state.span, gap: state.gap });
		if (raw === null) return;
		const result = limitSpan(limits, raw);
		if (result.span === state.last && (result.reason || null) === state.reason) return;
		state.last = result.span;
		state.reason = result.reason || null;
		onResizeLive?.(name, result);
	};
	const finishResize = cancelled => {
		const state = resize.current;
		resize.current = null;
		stopEscape.current?.();
		stopEscape.current = null;
		if (state) onResizeEnd?.(name, !cancelled && state.last !== state.start ? state.last : null);
	};
	const onResizeUp = (event, cancelled = false) => {
		try { event.currentTarget.releasePointerCapture?.(event.pointerId); } catch (_) { /* never captured */ }
		finishResize(cancelled);
	};
	// A cell that goes away mid-resize takes its key listener with it.
	useEffect(() => () => stopEscape.current?.(), []);

	const classes = [
		'form-layout-editor__element',
		cell ? 'form-layout-editor__element--cell' : '',
		cell?.newRow ? 'form-layout-editor__element--new-row' : '',
		Tag === 'div' ? 'form-layout-editor__element--nested' : '',
		isDragging ? 'form-layout-editor__element--dragging' : '',
		entry.unknown ? 'form-layout-editor__element--unknown' : '',
		width?.selected ? 'form-layout-editor__element--selected' : '',
		active ? 'form-layout-editor__element--active' : '',
		live ? 'form-layout-editor__element--resizing' : '',
		drop ? 'form-layout-editor__element--drop' : '',
	].filter(Boolean).join(' ');

	return (
		<Tag
			ref={setRefs}
			className={classes}
			style={{
				...(cell ? { '--span': cell.span } : null),
				...(cell && Tag === 'li' ? { '--at': index } : null),
				transform: CSS.Translate.toString(transform),
			}}
			data-element={name}
			data-unknown={entry.unknown ? 'true' : undefined}
			data-drop={drop ? drop.side : undefined}
			data-axis={drop ? drop.axis : undefined}
			onPointerDownCapture={onActivate ? () => onActivate(name) : undefined}
			onFocusCapture={onActivate ? () => onActivate(name) : undefined}
		>
			<div className="form-layout-editor__element-row">
				{disabled ? null : (
					<button
						type="button"
						ref={setActivatorNodeRef}
						className="form-layout-editor__grip"
						aria-label={`Move ${label}`}
						{...attributes}
						{...listeners}
						onKeyDown={event => onGripKeyDown?.(event, name)}
						onBlur={onGripBlur}
					>
						<DRAG_HANDLE aria-hidden="true" />
					</button>
				)}
				<span className="form-layout-editor__element-label" title={label}>
					{label}
					{entry.element?.options?.required ? <span className="required-indicator" aria-label="required">*</span> : null}
				</span>
				<span className="form-layout-editor__meta">
					{entry.unknown ? (
						<Tooltip title="This card's layout places a field this page does not know about. It keeps its place; if the card no longer has it, the next save drops it.">
							<span className="form-layout-editor__unknown">Unknown field</span>
						</Tooltip>
					) : <DataTypeBadge dataType={entry.element?.dataType} size="sm" />}
					{origin ? <span className="form-layout-editor__origin">{origin}</span> : null}
					{width ? (
						<button
							type="button"
							className={`form-layout-editor__width${width.flagged ? ' form-layout-editor__width--flagged' : ''}`}
							aria-expanded={width.selected}
							aria-controls={width.panelId}
							aria-label={`Width of ${label}: ${width.summary}${width.flagged ? ', outside its limits' : ''}`}
							onClick={width.onToggle}
						>
							{width.summary}
							{width.flagged ? <span className="form-layout-editor__width-flag">Outside limits</span> : null}
						</button>
					) : null}
				</span>
				{disabled ? null : (
					<Dropdown
						trigger={['click']}
						placement="bottomRight"
						menu={{ items: menuItems, onClick: ({ key }) => onMenu(name, key) }}
					>
						<Button type="text" size="small" icon={<KEBAB />} aria-label={`${label} actions`} className="form-layout-editor__element-menu" />
					</Dropdown>
				)}
			</div>
			{live ? (
				<span className={`form-layout-editor__readout${live.reason ? ' form-layout-editor__readout--limit' : ''}`} aria-hidden="true">
					{live.span} of {COLUMNS}
				</span>
			) : null}
			{resizable && limits.min < limits.max ? (
				<span
					className="form-layout-editor__resize"
					aria-hidden="true"
					data-resize={name}
					onPointerDown={onResizeDown}
					onPointerMove={onResizeMove}
					onPointerUp={onResizeUp}
					onPointerCancel={event => onResizeUp(event, true)}
				/>
			) : null}
		</Tag>
	);
}

/** A group's element list: a drop target even when empty. */
function DropList({ id, children, empty, emptyText, grid = false, aiming = false, dragging = false, resizing = false }) {
	const { setNodeRef } = useDroppable({ id, data: { groupId: groupIdOf(id) } });
	return (
		<div className="form-layout-editor__canvas">
			<ul
				ref={setNodeRef}
				data-list={id}
				className={[
					'form-layout-editor__list',
					grid ? 'form-layout-editor__list--grid' : '',
					aiming ? 'form-layout-editor__list--over' : '',
					dragging ? 'form-layout-editor__list--dragging' : '',
					resizing ? 'form-layout-editor__list--resizing' : '',
				].filter(Boolean).join(' ')}
			>
				{children}
				{empty ? <li className="form-layout-editor__empty">{emptyText}</li> : null}
			</ul>
		</div>
	);
}

export default function FormLayoutModal({
	open,
	onClose,
	card,
	// The SAVED card's merged elements: [{ element, fromCard, fromSet }] in
	// definition order (utils/cards mergeChainEntries over the resolved chain).
	entries = [],
	// The card cache, to name the ancestor a layout is inherited from.
	cardsById = {},
	onSaved,
}) {
	const { message, modal } = AntdApp.useApp();
	const cardId = normalizeId(card?._id);

	const [state, setState] = useState(null);       // { own, resolved } once loaded
	const [loadError, setLoadError] = useState(null);
	// The author's edits and the steps back through them: `draft` is null while
	// there are none — the body on screen is then the SEED (below), in the same
	// render the load lands in. The history is cleared with the draft.
	// (hooks/useUndoHistory: its `present` is the draft, null until an edit; a
	// null counts as the seed when two values are compared.)
	const seedRef = useRef(null);
	const sameDraft = useCallback((a, b) => {
		const left = a ?? seedRef.current;
		const right = b ?? seedRef.current;
		return left === right || JSON.stringify(left) === JSON.stringify(right);
	}, []);
	const history = useUndoHistory(null, { same: sameDraft });
	const draft = history.present;
	const { reset: resetDraft } = history;
	const resetHistory = useCallback(() => resetDraft(null), [resetDraft]);
	const [customizing, setCustomizing] = useState(false);
	const [saving, setSaving] = useState(false);
	const [saveError, setSaveError] = useState(null);
	// The element whose width controls are open (one at a time).
	const [selected, setSelected] = useState(null);
	// The element the author last touched: its resize handle is the one shown
	// on a touch screen, where there is no hover to reveal it.
	const [active, setActive] = useState(null);
	// A resize in progress: the element, the width it is being drawn at and
	// why it stopped there ({ name, span, reason }).
	const [liveResize, setLiveResize] = useState(null);
	// A drag in progress: the element and where it would land.
	const [drag, setDrag] = useState(null);
	// Why the last key or drag stopped at a limit, and what a screen reader
	// is told about the last move.
	const [limitNote, setLimitNote] = useState(null);
	// Why the last drop on the Left out box was refused (KPR-1211).
	const [leftOutNote, setLeftOutNote] = useState(null);
	const [announcement, setAnnouncement] = useState('');
	const dragRef = useRef(null);
	const pointer = useRef({ x: 0, y: 0 });
	const focusAfter = useRef(null);

	const byName = useMemo(
		() => new Map((entries || []).filter(entry => entry?.element?.name).map(entry => [entry.element.name, entry])),
		[entries]
	);
	const names = useMemo(() => [...byName.keys()], [byName]);
	// The elements as the layout is held to them: by name, for the limits.
	const defsByName = useMemo(() => new Map([...byName].map(([name, entry]) => [name, entry.element])), [byName]);
	const allElements = useMemo(() => [...defsByName.values()], [defsByName]);
	// What the grid arithmetic needs to know of the card (utils/formGridEditor).
	const gridCtx = useMemo(() => ({ defs: defsByName }), [defsByName]);

	// What the card reads as with NO layout — the editor's starting point
	// when nothing up the chain has one.
	const automatic = useMemo(
		() => formLayoutFromPanels(groupElementsIntoPanels(entries, { getElement: entry => entry.element }), card?.name),
		[entries, card?.name]
	);

	const load = useCallback(() => {
		if (!open || !cardId) return;
		setState(null);
		resetHistory();
		setLoadError(null);
		setSaveError(null);
		setCustomizing(false);
		setSelected(null);
		setActive(null);
		setLiveResize(null);
		setDrag(null);
		setLimitNote(null);
		setLeftOutNote(null);
		setAnnouncement('');
		getFormLayout(cardId)
			.then(next => setState(next))
			.catch(error => setLoadError(error));
	}, [open, cardId, resetHistory]);

	useEffect(load, [load]);

	// Closing the editor clears its history: a reopened one starts from what
	// is stored.
	useEffect(() => {
		if (!open) resetHistory();
	}, [open, resetHistory]);

	const own = state?.own || null;
	const resolved = state?.resolved || null;
	const inheritedFrom = resolved && !own ? normalizeId(resolved.card_id) : null;
	const mode = !state ? null : own ? 'own' : inheritedFrom ? 'inherited' : 'automatic';
	const editable = mode === 'own' || mode === 'automatic' || (mode === 'inherited' && customizing);
	const parentCard = inheritedFrom ? cardsById?.[inheritedFrom] || null : null;
	// The ancestor a reset would fall back to: only knowable from here when
	// this card's own row is what resolved (the API answers the nearest).
	//
	// The card's OWN stored names are kept whole, even ones `entries` (this
	// browser's card cache) does not know: the cache may be behind the card
	// — an element set or a parent not loaded, a change made elsewhere — and
	// filtering against it silently moved a real field out of its group on
	// the next save. They render in place as "Unknown field" and go back as
	// they were; a name the card truly lost is the API's to drop (the stored
	// row held it, so the save succeeds without it). An INHERITED layout is
	// still filtered: the child has no stored row to excuse a lost name, so
	// the API would refuse the whole override over it (unknown_element).
	//
	// A stored layout opens with the end of every date range it does not place
	// right after its start, exactly where the form draws it (KPR-626, k02), and
	// that is the base an edit is measured from: opening it is not an edit.
	//
	// A left-out name this card can no longer leave out (made required, or its
	// own now) opens unplaced, in Other fields, where the form draws it — and
	// that too is the base, not an edit; one the cache cannot describe is kept
	// as it was, like a placed one (KPR-1211).
	const eligibleLeftOut = useCallback((body) => {
		const list = leftOutList(body);
		if (!list.length) return body;
		const kept = list.filter((name) => {
			const entry = byName.get(name);
			return !entry || (Boolean(entry.fromCard) && !mustStayOnForm(entry.element));
		});
		if (kept.length === list.length) return body;
		const { leftOut, ...rest } = body;
		return kept.length ? { ...rest, leftOut: kept } : rest;
	}, [byName]);
	const ownNormalized = useMemo(
		() => (own ? eligibleLeftOut(placeRangeEnds(normalizeFormLayoutBody(own.body), allElements)) : null),
		[own, allElements, eligibleLeftOut]
	);
	const seed = useMemo(() => {
		if (!state) return { groups: [] };
		if (ownNormalized) return ownNormalized;
		if (resolved) return eligibleLeftOut(placeRangeEnds(normalizeFormLayoutBody(resolved.body, names), allElements));
		return automatic;
	}, [state, ownNormalized, resolved, names, automatic, allElements, eligibleLeftOut]);
	// The names an own layout places or leaves out that the cache cannot describe.
	const unknownNames = useMemo(() => {
		if (!ownNormalized) return new Set();
		return new Set([...placedFormNames(ownNormalized), ...leftOutList(ownNormalized)].filter(name => !byName.has(name)));
	}, [ownNormalized, byName]);
	const keepNames = useMemo(() => [...names, ...unknownNames], [names, unknownNames]);

	const body = draft ?? seed;
	seedRef.current = seed;
	// Every edit is a step in the history (undo and redo): `key` joins edits of
	// one kind in quick succession into one step (typing a title), and an edit
	// that changes nothing is not a step. `label` is what undo says it undid.
	const commit = history.commit;
	const setBody = useCallback((update, { key = null, label = 'change' } = {}) => {
		commit(current => {
			const base = current ?? seedRef.current;
			const next = typeof update === 'function' ? update(base) : update;
			return sameDraft(next, base) ? current : next;
		}, { key, label });
	}, [commit, sameDraft]);
	const undo = useCallback(() => {
		setLimitNote(null);
		const done = history.undo();
		setAnnouncement(done ? `Undid ${done.label}.` : 'Nothing to undo.');
	}, [history.undo]); // eslint-disable-line react-hooks/exhaustive-deps
	const redo = useCallback(() => {
		setLimitNote(null);
		const done = history.redo();
		setAnnouncement(done ? `Redid ${done.label}.` : 'Nothing to redo.');
	}, [history.redo]); // eslint-disable-line react-hooks/exhaustive-deps

	// While a resize is under way the body is drawn with the width it is being
	// dragged to, so the neighbours reflow as they will; the step in the
	// history is taken once, when the edge is let go.
	const viewBody = useMemo(
		() => (liveResize ? setFormElementPlacement(body, liveResize.name, { span: liveResize.span }) : body),
		[body, liveResize]
	);

	const placed = placedFormNames(body);
	const leftOut = leftOutList(body);
	const leftOutSet = new Set(leftOut);
	const unplaced = names.filter(name => !placed.has(name) && !leftOutSet.has(name));
	// Whether this card inherits anything a form could leave out: the box is
	// drawn only then (a top-level card has nothing to leave out).
	const inheritsAny = (entries || []).some(entry => entry?.fromCard);
	const canLeaveOut = useCallback((name) => {
		const entry = byName.get(name);
		return Boolean(entry?.fromCard) && !mustStayOnForm(entry.element);
	}, [byName]);

	const problem = editable ? formLayoutProblem(body, defsByName) : null;
	// The width controls open only on an element the body still places.
	const openName = selected && placed.has(selected) ? selected : null;
	const dirty = mode === 'own'
		? !sameFormLayoutBody(body, ownNormalized)
		: mode === 'inherited' ? customizing : !sameFormLayoutBody(body, automatic);

	// Unsaved work is an edit on top of what opened — not `dirty`, which is
	// also true of an automatic layout nobody has touched yet.
	const edited = draft !== null && !sameFormLayoutBody(draft, seed);
	const { allowNavigation, guardDiscard } = useDirtyForm(edited, { enabled: open });
	// Cancel, the ✕, the mask and Esc all ask first when something was arranged.
	const handleCancel = useCallback(() => guardDiscard(() => onClose?.()), [guardDiscard, onClose]);

	// Undo and redo from the keyboard (hooks/useUndoShortcuts): anywhere in the
	// editor except a text field, which keeps its own, and never for a
	// dropdown or a confirmation open over it.
	useUndoShortcuts({
		enabled: open && editable,
		onUndo: undo,
		onRedo: redo,
		accepts: (target) => Boolean(target?.closest?.('.form-layout-modal')) && !target.closest('.ant-select-dropdown, .ant-popover, .ant-dropdown, .ant-modal-confirm'),
	});

	// The pointer drags; the keys have their own moves on the handle, so
	// dnd-kit's keyboard sensor is not used.
	const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));

	const groupOfName = useCallback((name) => {
		const group = (body.groups || []).find(g => groupNames(g).includes(name));
		if (group) return group.id;
		return leftOutList(body).includes(name) ? LEFT : OTHER;
	}, [body]);

	// The pointer, tracked for the length of a drag: which half of a cell it is
	// over decides before or after.
	const trackPointer = useCallback(event => { pointer.current = { x: event.clientX, y: event.clientY }; }, []);
	const stopTracking = useCallback(() => {
		document.removeEventListener('pointermove', trackPointer);
		dragRef.current = null;
		setDrag(null);
	}, [trackPointer]);
	useEffect(() => () => document.removeEventListener('pointermove', trackPointer), [trackPointer]);

	const targetFor = useCallback((dragName, over) => {
		if (!over) return null;
		const id = String(over.id);
		if (id.startsWith('group:')) {
			const groupId = groupIdOf(id);
			return groupId === OTHER || groupId === LEFT ? { groupId, over: null } : { groupId, over: null, side: 'after' };
		}
		const overName = nameOf(id);
		const groupId = over.data?.current?.groupId;
		if (overName === dragName || groupId === undefined) return null;
		// A row of "Other fields" (or of the Left out box): dropping anywhere on
		// it puts the field in that list.
		if (groupId === OTHER || groupId === LEFT) return { groupId, over: null };
		const list = [...document.querySelectorAll('[data-list]')].find(node => node.getAttribute('data-list') === containerId(groupId));
		const axis = list ? dropAxis({ cell: over.rect, list: list.getBoundingClientRect() }) : 'x';
		const side = dropSide({ rect: over.rect, x: pointer.current.x, y: pointer.current.y, axis });
		return { groupId, over: overName, side, axis };
	}, []);

	const handleDragStart = useCallback(({ active: dragged, activatorEvent }) => {
		const name = nameOf(dragged.id);
		pointer.current = { x: activatorEvent?.clientX ?? 0, y: activatorEvent?.clientY ?? 0 };
		document.addEventListener('pointermove', trackPointer);
		dragRef.current = { name, target: null };
		setDrag({ name, target: null });
		setLimitNote(null);
	}, [trackPointer]);

	// dnd-kit reports a move with the cell the pointer was over a frame ago, and
	// reports the new cell only in onDragOver: both aim, so the bar is never
	// left on the cell before the one the pointer has settled on.
	const handleDragMove = useCallback(({ over }) => {
		const current = dragRef.current;
		if (!current) return;
		const target = targetFor(current.name, over);
		if (JSON.stringify(target) === JSON.stringify(current.target)) return;
		current.target = target;
		setDrag({ name: current.name, target });
	}, [targetFor]);

	// The drop lands where the pointer is as it is let go (the last cell dnd-kit
	// named), which is where the bar was drawn unless it had not caught up.
	const handleDragEnd = useCallback(event => {
		const current = dragRef.current;
		stopTracking();
		if (!current) return;
		const target = event && 'over' in event ? targetFor(current.name, event.over) : current.target;
		if (!target) return;
		const { name } = current;
		const label = labelOf(defsByName.get(name)) || name;
		if (target.groupId === LEFT) {
			if (leftOutList(body).includes(name)) return;
			if (!canLeaveOut(name)) {
				const entry = byName.get(name);
				const text = entry?.fromCard
					? `${label} cannot be left out. ${stayReason(entry.element) || ''}`.trim()
					: `${label} is this card's own field: only a field a parent card gives it can be left out.`;
				setLeftOutNote(text);
				setAnnouncement(text);
				return;
			}
			setLeftOutNote(null);
			setBody(base => leaveOutFormElement(base, name), { label: 'leave out' });
			setAnnouncement(`${label} is left out of this form.`);
			return;
		}
		setLeftOutNote(null);
		if (target.groupId === OTHER) {
			setBody(base => moveFormElement(base, name, null), { label: 'move' });
			setAnnouncement(leftOutList(body).includes(name) ? `${label} is back on the form, in ${OTHER_FIELDS_LABEL}.` : `${label} left the groups.`);
			return;
		}
		setBody(base => planFormDrop(base, name, target, gridCtx), { label: 'move' });
		const group = (body.groups || []).find(g => g.id === target.groupId);
		setAnnouncement(`Moved ${label} to ${group?.title || 'a group'}.`);
	}, [body, byName, canLeaveOut, defsByName, gridCtx, setBody, stopTracking, targetFor]);

	const menuItemsFor = useCallback((name) => {
		const current = groupOfName(name);
		const groups = body.groups || [];
		const moveTo = groups.filter(g => g.id !== current).map(g => ({ key: `to:${g.id}`, label: `Move to ${g.title || 'untitled group'}` }));
		const inGroup = current !== OTHER && current !== LEFT;
		// Only an element this card INHERITS may be left out; one that must
		// stay says why, in the menu itself (a disabled item cannot be focused
		// to show a tooltip).
		const entry = byName.get(name);
		const reason = entry?.fromCard ? stayReason(entry.element) : null;
		const leaveOut = current !== LEFT && entry?.fromCard && !unknownNames.has(name)
			? [{
				key: 'leaveout',
				disabled: Boolean(reason),
				label: reason ? (
					<span className="form-layout-editor__menu-reason-item">
						Leave out of this form
						<span className="form-layout-editor__menu-reason">{reason}</span>
					</span>
				) : 'Leave out of this form',
			}]
			: [];
		return [
			...(inGroup ? [
				{ key: 'up', label: 'Move up' },
				{ key: 'down', label: 'Move down' },
			] : []),
			...moveTo,
			// An unknown name has no place in Other fields to go to (the
			// renderer ignores a name the card lacks): taking it out of its
			// group takes it out of the layout.
			...(inGroup ? [{ type: 'divider' }, { key: 'unplace', label: unknownNames.has(name) ? 'Remove from layout' : `Leave in ${OTHER_FIELDS_LABEL}` }] : []),
			...(current === LEFT ? [{ type: 'divider' }, { key: 'putback', label: unknownNames.has(name) ? 'Remove from layout' : 'Put back on the form' }] : []),
			...(leaveOut.length ? [...(inGroup ? [] : [{ type: 'divider' }]), ...leaveOut] : []),
		];
	}, [body, byName, groupOfName, unknownNames]);

	const handleMenu = useCallback((name, key) => {
		setLimitNote(null);
		setLeftOutNote(null);
		if (key === 'leaveout') {
			if (!canLeaveOut(name)) return;
			setBody(current => leaveOutFormElement(current, name), { label: 'leave out' });
			setAnnouncement(`${labelOf(defsByName.get(name)) || name} is left out of this form.`);
			return;
		}
		if (key === 'putback') {
			setBody(current => putBackFormElement(current, name), { label: 'put back' });
			setAnnouncement(`${labelOf(defsByName.get(name)) || name} is back on the form, in ${OTHER_FIELDS_LABEL}.`);
			return;
		}
		setBody(current => {
			if (key === 'up') return nudgeFormElement(current, name, -1);
			if (key === 'down') return nudgeFormElement(current, name, 1);
			if (key === 'unplace') return moveFormElement(current, name, null);
			if (key.startsWith('to:')) return moveFormElement(current, name, key.slice(3));
			return current;
		}, { label: 'move' });
	}, [canLeaveOut, defsByName, setBody]);

	// ---- the keys on a handle: arrows move, Shift with Left or Right resizes

	const limitsOf = useCallback(name => {
		const element = defsByName.get(name);
		return element ? widthLimits(element) : null;
	}, [defsByName]);

	const drawnSpanOf = useCallback((name) => {
		const group = (body.groups || []).find(g => groupNames(g).includes(name));
		const cell = group ? groupCells(group, gridCtx).find(c => c.name === name) : null;
		return cell ? cell.span : null;
	}, [body, gridCtx]);

	const positionText = useCallback((next, name) => {
		const group = (next.groups || []).find(g => groupNames(g).includes(name));
		if (!group) return '';
		const list = groupNames(group);
		return `${labelOf(defsByName.get(name)) || name} is now ${list.indexOf(name) + 1} of ${list.length} in ${group.title || 'the group'}.`;
	}, [defsByName]);

	const handleGripKey = useCallback((event, name) => {
		if (!editable || event.metaKey || event.ctrlKey || event.altKey) return;
		const direction = ARROWS[event.key];
		if (!direction) return;
		if (!placed.has(name)) return;
		event.preventDefault();
		focusAfter.current = name;
		const label = labelOf(defsByName.get(name)) || name;

		if (event.shiftKey) {
			if (event.key === 'ArrowUp' || event.key === 'ArrowDown') return;
			const limits = limitsOf(name);
			const current = drawnSpanOf(name);
			if (!limits || current === null) {
				setLimitNote({ name, text: `${label} has no width to change here.` });
				return;
			}
			const step = stepSpan(limits, current, direction);
			if (!step.changed) {
				const text = step.reason
					? `${step.reason}.`
					: step.limited === 'max' ? `${label} is already the whole row.` : `${label} is already one column wide.`;
				setLimitNote({ name, text });
				setAnnouncement(text);
				return;
			}
			setLimitNote(null);
			setBody(base => setFormElementPlacement(base, name, { span: step.span }), { label: 'width change' });
			setAnnouncement(`${label} is now ${step.span} of ${COLUMNS} columns.`);
			return;
		}

		const next = stepFormElement(body, name, direction, gridCtx);
		if (next === body || JSON.stringify(next) === JSON.stringify(body)) {
			const text = `${label} cannot move ${direction < 0 ? 'earlier' : 'later'}.`;
			setLimitNote({ name, text });
			setAnnouncement(text);
			return;
		}
		setLimitNote(null);
		setBody(next, { label: 'move' });
		setAnnouncement(positionText(next, name));
	}, [editable, body, placed, defsByName, gridCtx, limitsOf, drawnSpanOf, positionText, setBody]);

	// A move across groups remounts the handle: put the keyboard back on it.
	useEffect(() => {
		const name = focusAfter.current;
		if (!name) return;
		focusAfter.current = null;
		const grip = [...document.querySelectorAll('.form-layout-modal [data-element]')]
			.find(node => node.getAttribute('data-element') === name)?.querySelector('.form-layout-editor__grip');
		if (grip && document.activeElement !== grip) grip.focus();
	});

	// ---- the handle on a cell's right edge

	const handleResizeStart = useCallback(name => {
		setActive(name);
		setLimitNote(null);
		setLiveResize({ name, span: drawnSpanOf(name), reason: null });
	}, [drawnSpanOf]);
	const handleResizeLive = useCallback((name, result) => {
		setLiveResize({ name, span: result.span, reason: result.reason || null });
	}, []);
	const handleResizeEnd = useCallback((name, span) => {
		setLiveResize(null);
		if (span === null || span === undefined) return;
		setBody(base => setFormElementPlacement(base, name, { span }), { label: 'width change' });
		setAnnouncement(`${labelOf(defsByName.get(name)) || name} is now ${span} of ${COLUMNS} columns.`);
	}, [defsByName, setBody]);

	const save = async () => {
		const next = normalizeFormLayoutBody(body, keepNames);
		const refusal = formLayoutProblem(next);
		if (refusal) { setSaveError(refusal); return; }
		setSaving(true);
		setSaveError(null);
		try {
			await putFormLayout(cardId, next);
			invalidateFormLayouts();
			message.success('Form layout saved.');
			allowNavigation();
			onSaved?.();
			onClose?.();
		} catch (error) {
			setSaveError(error?.response?.data?.message || 'Saving the form layout failed.');
		} finally {
			setSaving(false);
		}
	};

	const reset = async () => {
		setSaving(true);
		setSaveError(null);
		try {
			await deleteFormLayout(cardId);
			invalidateFormLayouts();
			message.success('Form layout reset.');
			allowNavigation();
			onSaved?.();
			onClose?.();
		} catch (error) {
			setSaveError(error?.response?.data?.message || 'Resetting the form layout failed.');
		} finally {
			setSaving(false);
		}
	};

	// Reset drops this card's row; asked first, since the arrangement goes
	// with it (History can put it back: `cardLayout.delete`).
	const confirmReset = () => confirmDestructive(modal, {
		title: 'Reset this form layout?',
		content: `This card's arrangement is removed and the ${card?.parentCardId ? 'inherited' : 'automatic'} layout applies again. History can restore it.`,
		okText: 'Reset',
		onOk: reset,
	});

	// The preview is the READ VIEW an item of this card gets, over believable
	// values, laid out by the body being edited — the same renderer, so it
	// cannot disagree with the pane.
	// A fresh item has nothing in a left-out field, so the preview leaves those
	// samples out — otherwise it would draw the Left out panel no new record
	// gets (KPR-1211).
	const sampleValues = useMemo(() => sampleElementValues(entries.map(entry => entry.element)), [entries]);
	const leftOutKey = leftOut.join('\n');
	const previewItem = useMemo(() => {
		if (!cardId) return null;
		const elements = { ...sampleValues };
		leftOutKey.split('\n').filter(Boolean).forEach((name) => { delete elements[name]; });
		return { _id: 'form-layout-preview', card_id: cardId, elements };
	}, [cardId, sampleValues, leftOutKey]);
	const previewDefs = useMemo(() => entries.map(entry => entry.element), [entries]);

	// What a placed row says about its width: the chip. An element in "Other
	// fields" has no layout entry to carry one, and a name this page cannot
	// describe has no limits to hold it to: neither gets a chip.
	const widthFor = (name, entry) => {
		const placement = placementOf(viewBody, name);
		if (!placement || entry.unknown) return null;
		const state = widthState(entry.element, placement.span);
		const selectedHere = openName === name;
		return {
			selected: selectedHere,
			flagged: Boolean(state.flag),
			summary: widthSummary(state, placement.newRow),
			panelId: `form-width-panel-${name}`,
			onToggle: () => setSelected(selectedHere ? null : name),
		};
	};

	// The controls an open chip shows. They sit under the group's canvas, not in
	// the cell: a cell can be two columns wide.
	const widthPanel = (name) => {
		const entry = byName.get(name);
		const placement = placementOf(viewBody, name);
		if (!entry || !placement) return null;
		return (
			<FormWidthControl
				element={entry.element}
				span={placement.span}
				newRow={placement.newRow}
				disabled={!editable}
				notice={editable ? null : 'This layout is inherited. Customize it for this card to change widths.'}
				onChange={patch => { setLimitNote(null); setBody(current => setFormElementPlacement(current, name, patch), { label: 'field change' }); }}
			/>
		);
	};

	const entryFor = name => byName.get(name)
		|| (unknownNames.has(name) ? { element: { name }, fromCard: null, fromSet: null, unknown: true } : null);

	const dropAt = (name) => {
		const target = drag?.target;
		return target && target.over === name && target.groupId !== OTHER && target.groupId !== LEFT ? { side: target.side, axis: target.axis || 'x' } : null;
	};

	const renderCell = (name, { groupId, index, cell }) => {
		const entry = entryFor(name);
		if (!entry) return null;
		const live = liveResize && liveResize.name === name ? liveResize : null;
		return (
			<ElementCell
				key={name}
				entry={entry}
				groupId={groupId}
				index={index}
				cell={cell}
				limits={cell && cell.kind === 'field' ? limitsOf(name) : null}
				disabled={!editable}
				menuItems={menuItemsFor(name)}
				onMenu={handleMenu}
				width={widthFor(name, entry)}
				drop={dropAt(name)}
				live={live}
				active={active === name}
				onActivate={setActive}
				onGripKeyDown={handleGripKey}
				onGripBlur={() => setLimitNote(null)}
				onResizeStart={handleResizeStart}
				onResizeLive={handleResizeLive}
				onResizeEnd={handleResizeEnd}
			/>
		);
	};

	// A group's canvas: each element a cell drawn at the width the form gives
	// it, in the order the form lays them out (the two halves of a date range
	// are two cells, as the form draws two fields).
	const renderGroupCells = (group) => {
		const viewGroup = (viewBody.groups || []).find(g => g.id === group.id) || group;
		const cells = new Map(groupCells(viewGroup, gridCtx).map(cell => [cell.name, cell]));
		const rows = [];
		groupNames(group).forEach((name, position) => {
			const cell = cells.get(name);
			if (!cell) return;
			rows.push(renderCell(name, { groupId: group.id, index: position, cell }));
		});
		return rows;
	};

	const noteFor = (group) => {
		const names = groupNames(group);
		if (liveResize?.reason && names.includes(liveResize.name)) return `${liveResize.reason}.`;
		if (limitNote && names.includes(limitNote.name)) return limitNote.text;
		return null;
	};

	const panelFor = (group) => {
		if (!openName) return null;
		const names = groupNames(group);
		if (!names.includes(openName)) return null;
		const at = names.indexOf(openName);
		return (
			<li
				key="width-panel"
				className="form-layout-editor__width-panel"
				id={`form-width-panel-${openName}`}
				data-for={openName}
				style={{ '--at': at }}
			>
				{widthPanel(openName)}
			</li>
		);
	};

	const aiming = (listId) => {
		const target = drag?.target;
		if (!target) return false;
		if (target.groupId === OTHER) return listId === containerId(OTHER);
		if (target.groupId === LEFT) return listId === containerId(LEFT);
		return target.over === null && listId === containerId(target.groupId);
	};

	return (
		<Modal
			open={open}
			onCancel={handleCancel}
			title="Form layout"
			width={1200}
			className="form-layout-modal"
			// Esc puts a drag or a resize back (dnd-kit; the cell's own listener): it
			// must not also close the dialog.
			keyboard={!drag && !liveResize}
			footer={[
				mode === 'own' ? (
					<Button key="reset" danger onClick={confirmReset} disabled={saving}>
						Reset to {card?.parentCardId ? 'inherited' : 'automatic'}
					</Button>
				) : null,
				<Button key="cancel" onClick={handleCancel}>Cancel</Button>,
				<Tooltip key="save" title={problem || (!dirty && editable ? 'No changes' : '')}>
					<Button type="primary" loading={saving} disabled={!editable || !dirty || Boolean(problem)} onClick={save}>
						Save
					</Button>
				</Tooltip>,
			].filter(Boolean)}
		>
			<DialogLoadState
				loaded={Boolean(state)}
				error={Boolean(loadError)}
				label="this card's form layout"
				onRetry={load}
				skeleton={<Skeleton variant='form-field' count={5} />}
				minHeight={320}
			>
				<>
					<div className="form-layout-editor__status" data-mode={mode}>
						{mode === 'own' ? (
							<p className="form-panel__hint">
								This card&rsquo;s own layout: the order and groups of its item form and read view.
								Child cards inherit it until they customize their own. Changes here apply immediately.
							</p>
						) : mode === 'inherited' ? (
							<div className="form-layout-editor__inherited">
								<span className="form-panel__hint">
									Inherited from{' '}
									{parentCard ? (
										<RecordBadge kind="card" record={parentCard} title={parentCard?.name || 'a parent card'} size="xs" />
									) : 'a parent card'}
									{customizing ? ' — customizing it for this card. Save to override it.' : '.'}
								</span>
								{customizing ? null : (
									<Button onClick={() => setCustomizing(true)}>Customize for this card</Button>
								)}
							</div>
						) : (
							<p className="form-panel__hint">
								Automatic: fields are grouped by each element&rsquo;s Group option, an element
								set&rsquo;s under the set&rsquo;s name. Arrange them here and save to fix the order.
								Changes here apply immediately.
							</p>
						)}
					</div>

					{saveError ? <Alert type="error" showIcon message={saveError} className="form-layout-editor__error" /> : null}

					{editable ? (
						<div className="form-layout-editor__toolbar">
							<p className="form-panel__hint form-layout-editor__hint">
								Drag a field by its handle to move it, and drag its right edge to make it wider or narrower.
								With a handle focused, the arrow keys move the field and Shift with Left or Right resizes it.
							</p>
							<UndoRedoButtons canUndo={history.canUndo} canRedo={history.canRedo} onUndo={undo} onRedo={redo} />
						</div>
					) : null}
					<div className="form-layout-editor__announce" role="status" aria-live="polite">{announcement}</div>

					<div className="form-layout-editor">
						<div className="form-layout-editor__groups">
							<DndContext
								sensors={sensors}
								collisionDetection={collideWithPointer}
								accessibility={DND_ACCESSIBILITY}
								onDragStart={handleDragStart}
								onDragMove={handleDragMove}
								onDragOver={handleDragMove}
								onDragEnd={handleDragEnd}
								onDragCancel={stopTracking}
							>
								{(body.groups || []).map((group, index, list) => {
									const names = groupNames(group);
									const note = noteFor(group);
									return (
										<section key={group.id} className="form-layout-editor__group" aria-label={`Group ${group.title || index + 1}`}>
											<div className="form-layout-editor__group-head">
												<Input
													value={group.title}
													maxLength={MAX_GROUP_TITLE}
													disabled={!editable}
													placeholder={index === 0 && card?.name ? card.name : 'Group title'}
													aria-label="Group title"
													status={editable && !(group.title || '').trim() ? 'error' : undefined}
													onChange={event => setBody(current => updateFormGroup(current, group.id, { title: event.target.value }), { key: `title:${group.id}`, label: 'group title' })}
												/>
												{editable ? (
													<span className="form-layout-editor__group-actions">
														<Button type="text" size="small" icon={<CHEVRON_UP />} aria-label={`Move ${group.title || 'group'} up`} disabled={index === 0} onClick={() => setBody(current => moveFormGroup(current, group.id, -1), { label: 'group move' })} />
														<Button type="text" size="small" icon={<CHEVRON_DOWN />} aria-label={`Move ${group.title || 'group'} down`} disabled={index === list.length - 1} onClick={() => setBody(current => moveFormGroup(current, group.id, 1), { label: 'group move' })} />
														<Button type="text" size="small" icon={<CLOSE />} aria-label={`Remove ${group.title || 'group'}`} onClick={() => setBody(current => removeFormGroup(current, group.id), { label: 'group removal' })} />
													</span>
												) : null}
											</div>
											<Input.TextArea
												value={group.description || ''}
												maxLength={MAX_GROUP_DESCRIPTION}
												disabled={!editable}
												autoSize={{ minRows: 1, maxRows: 4 }}
												placeholder="Description (optional)"
												aria-label="Group description"
												onChange={event => setBody(current => updateFormGroup(current, group.id, { description: event.target.value }), { key: `description:${group.id}`, label: 'group description' })}
											/>
											<fieldset className="form-layout-editor__collapsed" disabled={!editable}>
												<legend className="form-layout-editor__collapsed-legend">Start expanded</legend>
												<Checkbox
													checked={!groupCollapsed(group).form}
													disabled={!editable}
													onChange={event => setBody(current => setFormGroupCollapsed(current, group.id, 'form', !event.target.checked), { label: 'group setting' })}
												>
													In the form
												</Checkbox>
												<Checkbox
													checked={!groupCollapsed(group).view}
													disabled={!editable}
													onChange={event => setBody(current => setFormGroupCollapsed(current, group.id, 'view', !event.target.checked), { label: 'group setting' })}
												>
													In the read view
												</Checkbox>
											</fieldset>
											<DropList
												id={containerId(group.id)}
												grid
												empty={!names.length}
												emptyText={editable ? 'Drag fields here, or use a field’s ⋯' : 'No fields'}
												aiming={aiming(containerId(group.id))}
												dragging={Boolean(drag)}
												resizing={Boolean(liveResize)}
											>
												{renderGroupCells(group)}
												{panelFor(group)}
											</DropList>
											{note ? <p className="form-layout-editor__limit" role="status">{note}</p> : null}
										</section>
									);
								})}

								{editable ? (
									<Button icon={<PLUS />} onClick={() => setBody(current => addFormGroup(current), { label: 'new group' })} className="form-layout-editor__add">
										Add group
									</Button>
								) : null}

								<section className="form-layout-editor__group form-layout-editor__group--other" aria-label={OTHER_FIELDS_LABEL}>
									<div className="micro-label">{OTHER_FIELDS_LABEL}</div>
									<p className="form-panel__hint">
										Fields you leave out of the groups read here, after them &mdash; and so does any
										field a parent card or an element set adds later.
									</p>
									<DropList
										id={containerId(OTHER)}
										empty={!unplaced.length}
										emptyText="Every field is in a group."
										aiming={aiming(containerId(OTHER))}
										dragging={Boolean(drag)}
									>
										{unplaced.map(name => renderCell(name, { groupId: OTHER, index: 0, cell: null }))}
									</DropList>
								</section>

								{inheritsAny || leftOut.length ? (
									<section className="form-layout-editor__group form-layout-editor__group--other form-layout-editor__group--left-out" aria-label="Left out of this form">
										<div className="micro-label">Left out of this form</div>
										<p className="form-panel__hint">
											Fields this card inherits and does not use. The form leaves them out, and the
											read view shows one only when a record already holds a value. A required field
											cannot be left out.
										</p>
										<DropList
											id={containerId(LEFT)}
											empty={!leftOut.length}
											emptyText={editable ? 'Nothing is left out. Use an inherited field’s ⋯ to leave it out.' : 'Nothing is left out.'}
											aiming={aiming(containerId(LEFT))}
											dragging={Boolean(drag)}
										>
											{leftOut.map(name => renderCell(name, { groupId: LEFT, index: 0, cell: null }))}
										</DropList>
										{leftOutNote ? <p className="form-layout-editor__limit" role="status">{leftOutNote}</p> : null}
									</section>
								) : null}
							</DndContext>
						</div>

						<div className="form-layout-editor__preview" aria-label="Preview">
							<div className="micro-label">Preview</div>
							{previewItem ? (
								<FormLayoutPreview>
									<ItemReadView
										item={previewItem}
										card={card}
										elementDefs={previewDefs}
										showMeta={false}
										showTags={false}
										itemTags={false}
										formLayout={body}
										confirmations={false}
									/>
								</FormLayoutPreview>
							) : null}
						</div>
					</div>
				</>
			</DialogLoadState>
		</Modal>
	);
}
